import { useMemoriesStore } from '@/context/useMemoriesStore'
import { useTicketsStore } from '@/context/useTicketsStore'
import { orderKey } from '@/helpers/orders'
import { getMemories } from '@/services/Memories/getMemories'
import { getTickets } from '@/services/Wallet/getTickets'
import { Booking, Order, OrderStatus } from '@/types/booking'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

export const WALLET_QUERY_KEY = ['wallet']

export const fetchWalletData = async () => {
  const [walletRes, memories] = await Promise.all([getTickets(), getMemories()])
  return { walletRes, memories }
}

export const useWalletQuery = (enabled: boolean) => {
  const { setWallet, setWalletPending } = useTicketsStore()
  const { setMemories } = useMemoriesStore()

  const query = useQuery({
    queryKey: WALLET_QUERY_KEY,
    queryFn: fetchWalletData,
    enabled,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: 7500,
  })

  const { data, isFetching } = query

  useEffect(() => {
    setWalletPending(isFetching && !data)
  }, [isFetching, data, setWalletPending])

  useEffect(() => {
    if (!data) return
    const { walletRes, memories } = data
    const bookings = walletRes?.wallet ?? []
    // Keep the owning instance: an order can span two, and the optimistic
    // statuses are keyed by the pair.
    const allOrders = bookings.flatMap((b: Booking) =>
      (b.orders ?? []).map((o: Order) => ({
        ...o,
        instance_uid: o.instance_uid ?? b.instance_uid,
      })),
    ) as Order[]

    // Clear optimistic statuses for orders the server has caught up on
    const { clearOptimisticStatus, optimisticStatuses } =
      useTicketsStore.getState()
    allOrders.forEach((order) => {
      if (
        optimisticStatuses[orderKey(order.uid, order.instance_uid)] &&
        order.status !== OrderStatus.STANDARD
      ) {
        clearOptimisticStatus(order.uid, order.instance_uid)
      }
    })

    // setWallet derives walletOrders; upcoming/past come from selectors.
    setWallet({ bookings, more: walletRes?.more })
    setMemories(memories?.memories ?? [], memories?.hasMore ?? false)
  }, [data, setWallet, setMemories])

  return query
}

export const useInvalidateWallet = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: WALLET_QUERY_KEY })
}
